import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";

export function ChangePasswordForm({ forced = false, onSuccess }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const clientChecks = () => {
    const e = [];
    const p = form.new_password;
    if (!form.current_password) e.push("Inserisci la password attuale.");
    if (p.length < 12) e.push("Almeno 12 caratteri");
    if (!/[A-Z]/.test(p)) e.push("Almeno una lettera maiuscola");
    if (!/[a-z]/.test(p)) e.push("Almeno una lettera minuscola");
    if (!/\d/.test(p)) e.push("Almeno un numero");
    if (!/[!@#$%^&*()_+\-={}[\]:;"'<>,.?/\\|~` ]/.test(p)) e.push("Almeno un carattere speciale");
    if (p !== form.confirm_password) e.push("La conferma password non corrisponde.");
    if (p && p === form.current_password) e.push("La nuova password non può coincidere con quella attuale.");
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const cerrors = clientChecks();
    setErrors(cerrors);
    if (cerrors.length > 0) return;
    setSubmitting(true);
    try {
      const r = await api.post("/auth/change-password", form);
      toast.success(r.data.message || "Password aggiornata");
      onSuccess?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nel cambio password");
      setErrors([typeof detail === "string" ? detail : "Errore"]);
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      <div>
        <Label htmlFor="cp-current">Password attuale</Label>
        <Input data-testid="cp-current" id="cp-current" type="password" autoComplete="current-password"
          value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required />
      </div>
      <div>
        <Label htmlFor="cp-new">Nuova password</Label>
        <Input data-testid="cp-new" id="cp-new" type="password" autoComplete="new-password"
          value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required />
        <div className="text-xs text-slate-500 mt-1">Almeno 12 caratteri, con maiuscola, minuscola, numero e carattere speciale.</div>
      </div>
      <div>
        <Label htmlFor="cp-confirm">Conferma nuova password</Label>
        <Input data-testid="cp-confirm" id="cp-confirm" type="password" autoComplete="new-password"
          value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} required />
      </div>
      {errors.length > 0 && (
        <Alert variant="destructive" data-testid="cp-errors">
          <AlertTitle>Non riesco a salvare</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside text-xs">{errors.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </AlertDescription>
        </Alert>
      )}
      <div className="flex justify-end gap-2">
        <Button data-testid="cp-submit" type="submit" disabled={submitting} className="ama-btn-primary rounded-full">
          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <KeyRound className="w-4 h-4 mr-2" /> {forced ? "Imposta password e continua" : "Aggiorna password"}
        </Button>
      </div>
    </form>
  );
}

export default function ForcePasswordChange() {
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(160deg,#0a0a2e 0%, #3a0e5e 55%, #6f42c1 100%)" }}>
      <Card className="ama-card border-0 max-w-md w-full shadow-2xl" data-testid="force-password-change-page">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-white ama-gradient"><ShieldCheck className="w-5 h-5" /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Sicurezza account</div>
              <CardTitle className="font-heading">Cambio password obbligatorio</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Per motivi di sicurezza, prima di continuare devi impostare una nuova password personale.
            La password temporanea non sarà più valida al termine di questa operazione.
          </p>
          <ChangePasswordForm forced onSuccess={() => { toast.success("Password aggiornata. Effettua nuovamente l'accesso."); setTimeout(() => logout(), 700); }} />
          <div className="flex justify-between text-xs text-slate-500 pt-2 border-t">
            <Link to="/login" className="hover:text-[#d63384]">Torna al login</Link>
            <button onClick={logout} className="hover:text-[#d63384]" data-testid="force-cp-logout">Esci</button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
